import { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { IWorkOrder } from '../../../models';
import { TabType } from './tabTypes';

export interface TabSelectorEntity {
    title: string;
    id: string | number;
    isWorkOrder: boolean;
    isMeasurementPoint: boolean;
    index: number;
    isChecklistTab?: boolean;
}

interface Iprops {
    tabs: TabSelectorEntity[];
    t: TFunction;
    getWorkOrderTitle: (workOrder: IWorkOrder, t: TFunction) => string;
    setActiveTab: (tab: TabType | number | string) => void;
    activeTab: number | TabType | string;
}

const SelectorWrapper = styled.div`
    cursor: pointer;
    user-select: none;
    position: relative;
    flex: 1;
    min-width: 0;

    @media (min-width: 768px) {
        max-width: 320px;
    }
`;

const InputWrapper = styled.div`
    background: #ffffff;
    padding-left: 12px;
    padding-right: 12px;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    color: #013564;
    width: 100%;
    min-height: 41px;
    height: 41px;
    max-height: 41px;
    border-radius: 4px 4px 0px 0px;
    z-index: 99;
    position: relative;
`;

const ArrowSvg = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="7" fill="none" viewBox="0 0 11 7">
            <path
                stroke="#CCC"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M1 1.25l4.5 4.5 4.5-4.5"
            ></path>
        </svg>
    );
};

const Arrow = styled(ArrowSvg)`
    width: 9px;
    height: 9px;

    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    right: 12px;
`;

const Placeholder = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #007bc3;
    height: 100%;

    & > span {
        font-style: normal;
        font-weight: 800;
        font-size: 14px;
        color: #013564;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        @media (min-width: 414px) {
            font-size: 15px;
        }

        @media (min-width: 500px) {
            font-size: 16px;
        }
    }
`;

const Menu = styled.div<{ menuTop: number; menuLeft: number }>`
    background-color: #fff;
    max-height: 400px;
    z-index: 999;
    overflow-y: auto;
    position: fixed;
    top: ${(props: { menuTop: number; menuLeft: number }) => props.menuTop}px;
    left: 8px;
    right: 8px;
    border: 1px solid #f1f1f1;
    box-shadow: 0px 5px 6px rgba(0, 0, 0, 0.25);
    border-radius: 0 0 4px 4px;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding-top: 4px;
    padding-bottom: 8px;

    @media (min-width: 768px) {
        left: ${(props: { menuTop: number; menuLeft: number }) => props.menuLeft}px;
        right: auto;
        width: 320px;
    }
`;

const Dropdown = styled.div`
    position: absolute;
    left: 0;
    right: 0;
`;

const Item = styled.div`
    padding: 10px 12px;
    display: flex;
    align-items: center;
    font-style: normal;
    font-weight: ${(props: any) => (props.highlight ? '800' : '400')};
    font-size: 14px;
    line-height: 1.3;
    color: #007bc3;
    width: 100%;
    background-color: ${(props: any) => props.highlight && '#C1EDFF'};
    word-break: break-word;

    @media (min-width: 375px) {
        font-size: 15px;
    }

    @media (min-width: 414px) {
        font-size: 16px;
    }
`;

const Backdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100dvh;
    background-color: rgba(0, 0, 0, 0.2);
    z-index: 997;
`;

export default (props: Iprops) => {
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [hoveredIndex, setHoveredIndex] = React.useState<number>();
    const [menuTop, setMenuTop] = React.useState<number>(0);
    const [menuLeft, setMenuLeft] = React.useState<number>(0);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const placeholder: string = useMemo(() => {
        const activeTab: TabSelectorEntity | undefined = props.tabs.find(
            (tab: TabSelectorEntity) => tab.id === props.activeTab
        );
        if (!activeTab) {
            return 'Select from list';
        }

        return activeTab.title;
    }, [props.tabs, props.activeTab]);

    const handleOpen = () => {
        if (!isOpen && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setMenuTop(rect.bottom);
            setMenuLeft(rect.left);
        }
        setIsOpen(prev => !prev);
    };

    return (
        <SelectorWrapper ref={wrapperRef}>
            {isOpen && (
                <Backdrop
                    onClick={() => {
                        setIsOpen(false);
                    }}
                />
            )}
            <Dropdown>
                <InputWrapper onClick={handleOpen}>
                    <Placeholder>
                        <span>{placeholder}</span>
                        <Arrow />
                    </Placeholder>
                </InputWrapper>
                {isOpen && (
                    <Menu menuTop={menuTop} menuLeft={menuLeft}>
                        {props.tabs.map((tab: TabSelectorEntity, index: number) => {
                            return (
                                <Item
                                    key={tab.id}
                                    highlight={index === hoveredIndex || props.activeTab === tab.id}
                                    onMouseOver={() => {
                                        setHoveredIndex(index);
                                    }}
                                    onClick={() => {
                                        props.setActiveTab(tab.id);
                                        setIsOpen(false);
                                    }}
                                >
                                    {tab.title}
                                </Item>
                            );
                        })}
                    </Menu>
                )}
            </Dropdown>
        </SelectorWrapper>
    );
};
